import React from "react";
import { useNavigate } from "react-router-dom";
import { assetUrl } from "@/lib/api";
import { Aperture, Layers, Zap, ShieldCheck, HeartHandshake } from "lucide-react";

/**
 * Unified public campaign card.
 *
 * Shared by:
 *   - Homepage (`/`) — Trending Offers + All Live Offers grids
 *   - Explore (`/explore`) — main grid
 *
 * Design tokens (strict, must match Login/My Winnings/Offer pages):
 *   - Cream paper background: #F7F4EC
 *   - Ink border/text:        #0A0A0A
 *   - Gold accent / badge:    #F5A800
 *   - Headline font:          Anton (via inline style so nothing regresses)
 *   - Offset shadow:          4px 4px 0 #0A0A0A (hover deepens)
 *
 * Static offers route to `/offers/{id}`; everything else to `/play/{id}`.
 * Cards must be pure, presentational — no data fetching inside.
 */
export function CampaignCard({ campaign, size = "md" }) {
  const navigate = useNavigate();
  const c = campaign || {};

  // Belt-and-braces client-side expiry check. The Explore feed
  // already excludes expired campaigns server-side and the daily
  // scheduler flips their status to "expired", but a stale in-flight
  // response OR a cached SSR payload could still hand us one. When
  // that happens we render the card AT ALL only if the caller hasn't
  // hidden it further up the tree, and we DEFINITELY strip the
  // Verified / Trusted / Featured chrome so we never advertise an
  // expired offer as still-live-and-endorsed.
  const isExpired = React.useMemo(() => {
    if (c.status && String(c.status).toLowerCase() === "expired") return true;
    if (typeof c.is_expired === "boolean") return c.is_expired;
    if (!c.expires_at) return false;
    // Mirror backend `core/expiry.py` semantics: a bare-date at
    // midnight UTC (the wizard's default) is valid through end-of-day
    // IST (UTC+05:30). Anything with an explicit time-of-day is
    // treated verbatim.
    const parsed = new Date(c.expires_at);
    if (Number.isNaN(parsed.getTime())) return false;
    const isMidnightUtc = parsed.getUTCHours() === 0 && parsed.getUTCMinutes() === 0
      && parsed.getUTCSeconds() === 0 && parsed.getUTCMilliseconds() === 0;
    const cutoffMs = isMidnightUtc
      ? parsed.getTime() + (18 * 60 + 30) * 60 * 1000 - 1  // EOD-IST minus 1ms
      : parsed.getTime();
    return Date.now() > cutoffMs;
  }, [c.status, c.is_expired, c.expires_at]);

  const showVerified = !!c.is_verified && !isExpired;
  const showTrusted  = !!c.is_trusted  && !isExpired;
  const showFeatured = !!c.featured_rank && !isExpired;

  const handleOpen = () => {
    if (!c.id) return;
    if (c.game_type === "static_offer") {
      navigate(`/offers/${c.id}`);
    } else {
      navigate(`/play/${c.id}`);
    }
  };

  const badge = getGameBadge(c.game_type);

  // Larger card variant used for the hero "Trending" strip. Kept as a
  // size prop rather than two components so both grids stay in sync
  // whenever the visual language moves.
  const aspect = size === "lg" ? "aspect-[16/10]" : "aspect-video";

  return (
    <button
      type="button"
      onClick={handleOpen}
      className="group text-left w-full bg-white border-2 border-[#0A0A0A] rounded-2xl overflow-hidden transition-all duration-150 hover:-translate-y-0.5 hover:shadow-[6px_6px_0_#0A0A0A] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F5A800] focus-visible:ring-offset-2 focus-visible:ring-offset-[#F7F4EC]"
      style={{ boxShadow: "4px 4px 0 #0A0A0A" }}
      data-testid={`campaign-card-${c.game_type || "unknown"}`}
    >
      <div className={`relative w-full ${aspect} bg-[#F7F4EC] border-b-2 border-[#0A0A0A] overflow-hidden`}>
        {c.thumbnail_url ? (
          <img
            src={assetUrl(c.thumbnail_url)}
            alt=""
            className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-[1.02]"
            loading="lazy"
          />
        ) : (
          <div
            className="w-full h-full flex items-center justify-center"
            style={{
              background: `linear-gradient(135deg, ${c.primary_color || "#F5A800"} 0%, ${c.secondary_color || "#0A0A0A"} 100%)`,
            }}
          >
            <span
              className="text-white/90 text-xl sm:text-2xl md:text-4xl px-3 sm:px-4 text-center leading-tight break-words"
              style={{ fontFamily: '"Anton", "Impact", sans-serif' }}
            >
              {(c.name || c.vendor?.business || "CityDooh").slice(0, 22)}
            </span>
          </div>
        )}

        {/* Game-type pill — top-left, always visible */}
        <span
          className="absolute top-3 left-3 inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full bg-[#0A0A0A] text-[#F5A800] border-2 border-[#0A0A0A]"
          style={{ fontFamily: '"Anton", "Impact", sans-serif' }}
          data-testid={`campaign-card-badge-${c.game_type}`}
        >
          {badge.icon}
          {badge.label}
        </span>

        {/* Distance pill — only when publicCampaigns returned distance_km */}
        {c.vendor?.distance_km != null && (
          <span
            className="absolute top-3 right-3 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-white/95 border-2 border-[#0A0A0A] text-[#0A0A0A]"
            data-testid="campaign-card-distance"
          >
            {c.vendor.distance_km} km
          </span>
        )}

        {/* Verified stamp — blue "checkmark on white" (universal
            verified convention). Icon + colour make it distinct from
            the gold Instant Offer pill at top-left, which was the
            legibility complaint. */}
        {showVerified && (
          <span
            className="absolute bottom-3 left-3 inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded-full bg-white text-[#1D4ED8] border-2 border-[#1D4ED8] shadow-[2px_2px_0_#0A0A0A]"
            title="Verified by CityDooh"
            data-testid="campaign-card-verified"
          >
            {/* Filled blue check-in-circle — Twitter/Insta-style verify mark */}
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-3.5 w-3.5">
              <path d="M12 2l2.39 2.06 3.15-.24.24 3.15L20.94 9 20 12l.94 3-3.16 2.03-.24 3.15-3.15-.24L12 22l-2.39-2.06-3.15.24-.24-3.15L3.06 15 4 12l-.94-3 3.16-2.03.24-3.15 3.15.24L12 2Z"/>
              <path d="M9 12l2 2 4-4" fill="none" stroke="white" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Verified
          </span>
        )}

        {/* Trusted stamp — emerald "shield" (community trust). Distinct
            from both Instant Offer (gold) and Verified (blue). */}
        {showTrusted && (
          <span
            className={`absolute bottom-3 inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded-full bg-white text-[#047857] border-2 border-[#047857] shadow-[2px_2px_0_#0A0A0A] ${showVerified ? "left-[7.25rem]" : "left-3"}`}
            title="Trusted by CityDooh"
            data-testid="campaign-card-trusted"
          >
            {/* Shield with a heart — 'community-trusted' mark */}
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-3.5 w-3.5">
              <path d="M12 2 4 5v7c0 4.5 3.2 8.6 8 10 4.8-1.4 8-5.5 8-10V5l-8-3Z"/>
              <path d="M12 15.5 8.5 12 10 10.5l2 2 4-4L17.5 10 12 15.5Z" fill="white"/>
            </svg>
            Trusted
          </span>
        )}

        {/* Top-3 rank ribbon — A4 paid featured slot. Injected by the
            backend `sort_campaigns_with_features` helper when the slot
            matches the current listing scope. */}
        {showFeatured && [1, 2, 3].includes(Number(c.featured_rank)) && (
          <span
            className="absolute bottom-3 right-3 inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded-full bg-[#F5A800] text-[#0A0A0A] border-2 border-[#0A0A0A]"
            title={`Featured — Top ${c.featured_rank}`}
            data-testid={`campaign-card-featured-rank-${c.featured_rank}`}
            style={{ fontFamily: '"Anton", "Impact", sans-serif' }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-3 w-3"><path d="M12 2l2.4 6.5H21l-5.4 4.2L17.8 20 12 15.9 6.2 20l2.2-7.3L3 8.5h6.6z"/></svg>
            Top {c.featured_rank}
          </span>
        )}

        {/* Optional vendor logo chip — top-right of the image area
            (only when no distance pill is showing). Doesn't collide
            with the Verified/Trusted stamps at bottom-left. */}
        {c.logo_url && c.vendor?.distance_km == null && (
          <div
            className="absolute top-3 right-3 w-11 h-11 rounded-lg bg-white border-2 border-[#0A0A0A] overflow-hidden shadow-[3px_3px_0_#0A0A0A] flex items-center justify-center z-10"
            data-testid="campaign-card-logo"
          >
            <img
              src={assetUrl(c.logo_url)}
              alt=""
              className="w-full h-full object-contain p-0.5"
              loading="lazy"
            />
          </div>
        )}
      </div>

      <div className="p-4">
        <p
          className="text-lg md:text-xl leading-tight uppercase tracking-tight text-[#0A0A0A] line-clamp-2 font-black"
          style={{ fontFamily: '"Anton", "Impact", sans-serif', letterSpacing: "0.01em" }}
          data-testid="campaign-card-name"
        >
          {c.name || "Untitled campaign"}
        </p>
        {/* Static offer preview text — surfaces the actual reward so
            the card doesn't feel abstract. Only shows when the backend
            populated it (static_offer only). */}
        {c.offer_text && (
          <p
            className="mt-1.5 text-sm text-[#0A0A0A]/85 line-clamp-2 font-medium leading-snug"
            data-testid="campaign-card-offer-text"
          >
            {c.offer_text}
          </p>
        )}
        <p className="mt-1.5 text-xs text-[#0A0A0A]/60 truncate" data-testid="campaign-card-vendor">
          <span className="font-semibold text-[#0A0A0A]/80">{c.vendor?.business || "Local business"}</span>
          {(c.city || c.vendor?.city) ? (
            <span> · {c.city || c.vendor?.city}</span>
          ) : null}
        </p>
      </div>
    </button>
  );
}

/**
 * Small preset for the game-type badge, single source of truth so the
 * homepage and explore page can never drift on labels/icons.
 */
function getGameBadge(gameType) {
  if (gameType === "static_offer") {
    return { label: "Instant Offer", icon: <Zap className="h-3 w-3" strokeWidth={3} /> };
  }
  if (gameType === "scratch_card") {
    return { label: "Scratch", icon: <Layers className="h-3 w-3" strokeWidth={3} /> };
  }
  return { label: "Spin", icon: <Aperture className="h-3 w-3" strokeWidth={3} /> };
}

/** Skeleton card that matches the CampaignCard footprint exactly. */
export function CampaignCardSkeleton({ size = "md" }) {
  const aspect = size === "lg" ? "aspect-[16/10]" : "aspect-video";
  return (
    <div
      className="w-full bg-white border-2 border-[#0A0A0A] rounded-2xl overflow-hidden"
      style={{ boxShadow: "4px 4px 0 #0A0A0A" }}
      aria-hidden
    >
      <div className={`w-full ${aspect} bg-[#F7F4EC] animate-pulse`} />
      <div className="p-4 space-y-2">
        <div className="h-4 w-2/3 bg-[#0A0A0A]/10 rounded animate-pulse" />
        <div className="h-3 w-1/2 bg-[#0A0A0A]/10 rounded animate-pulse" />
      </div>
    </div>
  );
}

export default CampaignCard;
